import type { ReactNode } from "react";

import { Button, type ButtonVariant } from "@/components/ui/Button";
import { cx } from "@/lib/cx";

/**
 * DESIGN.md § Components `empty-state`: centred, max 420px, `spacing.12`
 * vertical padding, `{typography.heading}` headline, one `{typography.body}`
 * sentence in `{colors.ink-secondary}`, one primary button. No illustration,
 * no icon (§4.18 principle 7 — "an empty state without a button is a
 * defect"). Generic and reusable (Code Map: "the empty-state... components
 * (generic, reusable — not user-list-specific)") — every list this app ever
 * builds renders its own heading/body/action through this one component,
 * never a bespoke one.
 *
 * `action` is optional only for the one sanctioned exception EXPERIENCE.md
 * names ("Empty — good news", e.g. "Nothing needs your attention today.") —
 * `DataTable` never reaches for that exception on its own; every empty list
 * state it renders supplies an action.
 *
 * `action.variant` (Story 7.2): defaults to `"primary"` — every existing
 * caller (`PropertiesList.tsx`, `TenantsList.tsx`, ...) is a full, top-level
 * list whose own empty state is the page's one primary action, unchanged.
 * `PropertyRecord.tsx`'s own inline documents section is the first caller
 * to override it to `"ghost"` (the AC's own explicit words: "an empty-state
 * ghost Add button") — a record page already has its own header-level
 * primary action (Archive/Un-archive), so a second, embedded section's own
 * "Add" trigger is a lower-emphasis action, not a second primary button
 * competing on the same screen.
 */
export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
}

export function EmptyState({
  heading,
  body,
  action,
  className,
}: {
  heading: string;
  body: string;
  action?: EmptyStateAction;
  className?: string;
}): ReactNode {
  return (
    <div className={cx("mx-auto max-w-empty-state-max py-12 text-center", className)}>
      <h2 className="text-heading font-semibold text-ink">{heading}</h2>
      <p className="mt-2 text-body text-ink-secondary">{body}</p>
      {action ? (
        <Button className="mt-6" variant={action.variant ?? "primary"} onClick={action.onClick}>
          {action.label}
        </Button>
      ) : null}
    </div>
  );
}
